import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isBefore,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";

type DatePickerProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  min?: string;
  required?: boolean;
  id?: string;
};

const toDate = (value?: string) => {
  if (!value) return null;
  try {
    const parsed = parseISO(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  } catch {
    return null;
  }
};

export const DatePicker = ({
  value,
  onChange,
  placeholder = "Select date",
  min,
  required,
  id,
}: DatePickerProps) => {
  const selected = toDate(value);
  const minDate = toDate(min);
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState<Date>(selected || new Date());
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selected) {
      setMonth(selected);
    }
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(month));
    const end = endOfWeek(endOfMonth(month));
    return eachDayOfInterval({ start, end });
  }, [month]);

  const isDisabled = (day: Date) => {
    if (!minDate) return false;
    return isBefore(startOfDay(day), startOfDay(minDate));
  };

  const pickDay = (day: Date) => {
    if (isDisabled(day)) return;
    onChange(format(day, "yyyy-MM-dd"));
    setOpen(false);
  };

  return (
    <div className="date-picker" ref={rootRef}>
      <button
        id={id}
        type="button"
        className={`date-picker-trigger${value ? "" : " placeholder"}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span>
          {selected ? format(selected, "dd MMM yyyy") : placeholder}
        </span>
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      </button>

      {/* Keep a hidden required input so native form validation still works */}
      {required ? (
        <input
          className="date-picker-required"
          tabIndex={-1}
          required
          value={value}
          onChange={() => undefined}
          aria-hidden
        />
      ) : null}

      {open ? (
        <div className="date-picker-popover" role="dialog" aria-label="Choose date">
          <div className="date-picker-header">
            <button
              type="button"
              className="date-picker-nav"
              onClick={() => setMonth((prev) => subMonths(prev, 1))}
              aria-label="Previous month"
            >
              ‹
            </button>
            <div className="date-picker-month">{format(month, "MMMM yyyy")}</div>
            <button
              type="button"
              className="date-picker-nav"
              onClick={() => setMonth((prev) => addMonths(prev, 1))}
              aria-label="Next month"
            >
              ›
            </button>
          </div>

          <div className="date-picker-weekdays">
            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((label) => (
              <span key={label}>{label}</span>
            ))}
          </div>

          <div className="date-picker-grid">
            {days.map((day) => {
              const outside = !isSameMonth(day, month);
              const selectedDay = selected ? isSameDay(day, selected) : false;
              const disabled = isDisabled(day);
              const today = isSameDay(day, new Date());
              return (
                <button
                  key={day.toISOString()}
                  type="button"
                  className={[
                    "date-picker-day",
                    outside ? "outside" : "",
                    selectedDay ? "selected" : "",
                    today ? "today" : "",
                    disabled ? "disabled" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  disabled={disabled}
                  onClick={() => pickDay(day)}
                >
                  {format(day, "d")}
                </button>
              );
            })}
          </div>

          <div className="date-picker-footer">
            <button
              type="button"
              className="text-btn"
              onClick={() => {
                onChange("");
                setOpen(false);
              }}
            >
              Clear
            </button>
            <button
              type="button"
              className="text-btn"
              disabled={Boolean(minDate && isBefore(startOfDay(new Date()), startOfDay(minDate)))}
              onClick={() => {
                const today = format(new Date(), "yyyy-MM-dd");
                if (min && today < min) return;
                onChange(today);
                setOpen(false);
              }}
            >
              Today
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};
